import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { fetchActivePartnerCategories } from '@/features/profiles/modules/partner/partner-categories.api';
import { getApiErrorCode } from '@/lib/utils/error.utils';
import { PARTNER_NOT_AVAILABLE_CODE } from '../trusted-partners.constants';
import {
  contactPerk,
  fetchDirectory,
  fetchLivePerks,
  fetchPartnerDetail,
  recordEmailClick,
  recordSchedulingClick,
  redeemPerk,
  type DirectoryQuery,
} from '../api/trusted-partners.api';

export const directoryQueryKey = (query: DirectoryQuery) => ['trusted-partners', 'directory', query];
export const partnerDetailQueryKey = (entityId: string) => ['trusted-partners', 'detail', entityId];
export const livePerksQueryKey = ['trusted-partners', 'perks'] as const;
/** Same key the partner profile form uses, so React Query shares one cache entry and one request. */
export const PARTNER_CATEGORIES_QUERY_KEY = ['partner-categories', 'active'] as const;

/** Failed requests are retried at most this many times, except a definitive "no longer available" answer. */
const MAX_DETAIL_RETRIES = 2;

/** A page of listed partners (FR-111). Previous data is kept while the next page or filter loads. */
export function useDirectory(query: DirectoryQuery) {
  return useQuery({
    queryKey: directoryQueryKey(query),
    queryFn: () => fetchDirectory(query),
    placeholderData: keepPreviousData,
  });
}

/**
 * One partner's profile page. "No longer available" is an answer, not a transient
 * failure, so it is never retried; a network blip or 5xx still gets a couple of retries.
 */
export function usePartnerDetail(entityId: string) {
  return useQuery({
    queryKey: partnerDetailQueryKey(entityId),
    queryFn: () => fetchPartnerDetail(entityId),
    enabled: entityId.length > 0,
    retry: (failureCount, error) =>
      getApiErrorCode(error) !== PARTNER_NOT_AVAILABLE_CODE && failureCount < MAX_DETAIL_RETRIES,
  });
}

/** Active service categories, for the directory's category filter (FR-72). */
export function usePartnerCategories() {
  return useQuery({ queryKey: PARTNER_CATEGORIES_QUERY_KEY, queryFn: fetchActivePartnerCategories });
}

/** Every live, non-expired offer, newest first (FR-30). */
export function useLivePerks() {
  return useQuery({ queryKey: livePerksQueryKey, queryFn: fetchLivePerks });
}

/**
 * Fire-and-forget click logger (FR-30, FR-110, FR-118, FR-119). The link has already
 * opened by the time it runs, so a failure must never reach the member - but it is
 * reported, not swallowed (Card 11).
 */
function useClickLogger(log: (id: string) => Promise<void>) {
  return useMutation({
    mutationFn: (id: string) => log(id),
    onError: (error) => Sentry.captureException(error),
  });
}

/** Logs a Claim Offer click on an offer (FR-112). */
export function useRedeemPerk() {
  return useClickLogger(redeemPerk);
}

/** Logs a Contact Us click on an offer (FR-119); the member's mail app has already opened. */
export function useContactPerk() {
  return useClickLogger(contactPerk);
}

/** Logs a Schedule click on a partner profile (FR-110, FR-118). */
export function useSchedulingClick() {
  return useClickLogger(recordSchedulingClick);
}

/** Logs an email-contact click on a partner profile (FR-118). */
export function useEmailClick() {
  return useClickLogger(recordEmailClick);
}
